import PropTypes from 'prop-types';
import { cn } from '../utils/cn';

/**
 * Button component with variants following the design system.
 *
 * @example
 * <Button variant="primary">Save</Button>
 * <Button variant="secondary" size="sm">Cancel</Button>
 * <Button variant="ghost" disabled>Loading...</Button>
 */

const variants = {
  primary: [
    'bg-brand-500 text-white',
    'hover:bg-brand-600',
    'active:bg-brand-700',
    'disabled:bg-brand-300 disabled:cursor-not-allowed',
  ].join(' '),
  secondary: [
    'bg-surface text-ink border border-border',
    'hover:bg-gray-50',
    'active:bg-gray-100',
    'disabled:bg-gray-100 disabled:text-muted disabled:cursor-not-allowed',
  ].join(' '),
  ghost: [
    'bg-transparent text-ink',
    'hover:bg-gray-100',
    'active:bg-gray-200',
    'disabled:text-muted disabled:cursor-not-allowed',
  ].join(' '),
  destructive: [
    'bg-error-500 text-white',
    'hover:bg-error-600',
    'active:bg-red-700',
    'disabled:bg-red-300 disabled:cursor-not-allowed',
  ].join(' '),
};

const sizes = {
  sm: 'px-3 py-1.5 text-sm min-h-[32px]',
  md: 'px-4 py-2 text-sm min-h-[40px]',
  lg: 'px-6 py-3 text-base min-h-[48px]',
};

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  className,
  ...props
}) {
  return (
    <button
      className={cn(
        // Base styles
        'inline-flex items-center justify-center gap-2',
        'font-medium rounded-lg',
        'transition-colors duration-150',
        // Focus ring (accessibility)
        'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2',
        // Variant and size
        variants[variant],
        sizes[size],
        // Loading state
        loading && 'opacity-75 cursor-wait',
        // Custom classes
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg
          className="animate-spin h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </button>
  );
}

Button.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['primary', 'secondary', 'ghost', 'destructive']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  className: PropTypes.string,
};

export default Button;
